import { v4 as uuidv4 } from 'uuid';
import { recordApiResponse } from '@/utils/apiRecorder';
import { use401Handler } from '@/composables/use401Handler';

/**
 * Base HTTP client for Boomtown API. Used for login, userStatus, and other auth endpoints.
 * All requests use credentials: 'include' so the browser sends and receives the partitioned
 * session cookie (relay/BT_SESSION) in the same partition as the appv5 page. Do not strip or
 * override cookies; the response that sets the cookie must be received while the top-level
 * URL is appv5 (no full-page navigation to the API for login).
 */
interface RequestOptions {
    params?: Record<string, string>;
    body?: any;
    headers?: Record<string, string>;
}

export class HttpBaseClient {
    protected baseUrl: string;
    protected activeRequests: Set<AbortController> = new Set();

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * Base URL for requests. Use the configured API host (VITE_BOOMTOWN_API_HOST) whenever set
     * so that userStatus, login, and other auth calls go to the API host. The backend and IdP
     * expect the ACS at the API host (e.g. api.local-env.goboomtown.com); using the same host
     * for all auth requests ensures the session cookie is sent. Only use window.location.origin
     * when no API host is configured (e.g. true same-origin dev).
     */
    protected getEffectiveBaseUrl(): string {
        if (typeof window === 'undefined') return this.baseUrl.replace(/\/$/, '');
        const configured = this.baseUrl.replace(/\/$/, '');
        if (configured) return configured;
        const origin = window.location.origin;
        if (origin && origin !== 'null') return origin;
        return this.baseUrl.replace(/\/$/, '');
    }

    /**
     * Cancel all active requests
     * Called during logout to prevent in-flight requests from interfering with session cleanup
     */
    public cancelAllRequests(): void {
        this.activeRequests.forEach(controller => {
            controller.abort(new DOMException('Request cancelled', 'AbortError'));
        });
        this.activeRequests.clear();
    }

    protected async request<T>(
        method: string,
        path: string,
        options: RequestOptions = {},
        additionalHeaders: Record<string, string> = {}
    ): Promise<T> {
        // Build the query string if we have params
        let queryString = '';
        if (options.params) {
            const searchParams = new URLSearchParams();
            Object.entries(options.params).forEach(([key, value]) => {
                searchParams.append(key, value);
            });
            queryString = `?${searchParams.toString()}`;
        }

        let reqBody = options.body || undefined;

        // Special handling for URLSearchParams - don't stringify these
        const isFormData = reqBody instanceof URLSearchParams;

        if (reqBody && !isFormData) {
            // Only stringify if not URLSearchParams
            reqBody = JSON.stringify(reqBody);
        }

        // Combine base URL (if relative path) or use absolute path; use app origin when cross-origin to avoid CORS
        const base = this.getEffectiveBaseUrl();
        const fullUrl = `${base}/${path.replace(/^\//, '')}${queryString}`;

        // Set the correct content type if it's form data
        const contentType = isFormData ? 'application/x-www-form-urlencoded; charset=UTF-8' : 'application/json';

        const headers = {
            'content-type': contentType,
            'x-request-id': uuidv4(),
            ...options.headers,
            ...additionalHeaders,
        };

        // Create AbortController for this request
        const abortController = new AbortController();
        this.activeRequests.add(abortController);

        try {
            // credentials: 'include' required so session cookie is sent and Set-Cookie is stored in appv5 partition
            const response = await fetch(fullUrl, {
                method,
                headers,
                body: reqBody,
                credentials: 'include',
                mode: 'cors',
                signal: abortController.signal,
            });
            
            // Remove from active requests on completion
            this.activeRequests.delete(abortController);

            if (!response.ok) {
                if (response.status === 401) {
                    // Notify of session expiration. Navigation to /login is handled by
                    // auth.ts (handleAuthChange) so we don't redirect here — this base
                    // client is also used for auth endpoints where a 401 is expected
                    // (e.g. userStatus when not logged in).
                    const { handle401Error } = use401Handler();
                    handle401Error('accessing the API').catch(console.error);
                }

                // For 4xx client errors (excluding 401), try to surface the API's error
                // message instead of a generic "HTTP error! status: N" string.
                // 5xx errors keep the original format so existing status-code regex
                // checks (e.g. server-error tracking in Login.vue / auth.ts) still work.
                if (response.status >= 400 && response.status < 500 && response.status !== 401) {
                    try {
                        const errorBody = await response.json();
                        if (errorBody?.message) {
                            throw new Error(errorBody.message);
                        }
                    } catch (parseError) {
                        if (parseError instanceof Error && parseError.message !== `HTTP error! status: ${response.status}`) {
                            throw parseError;
                        }
                    }
                }

                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let responseData;
            try {
                responseData = await response.json();
            } catch (jsonError) {
                // Log the JSON parsing error with context
                console.error('Failed to parse JSON response:', {
                    url: fullUrl,
                    method,
                    status: response.status,
                    error: jsonError instanceof Error ? jsonError.message : String(jsonError)
                });
                
                // Try to get the raw text for debugging (limited to first 1000 chars)
                const rawText = await response.text().catch(() => 'Unable to read response text');
                console.error('Response preview:', rawText.substring(0, 1000));
                
                throw new Error(`Failed to parse JSON response: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}`);
            }

            // Record the API response
            const functionName = new Error().stack?.split('\n')[2]?.match(/at\s+(\w+)/)?.[1] || 'unknown';
            recordApiResponse(functionName, fullUrl, method, responseData);

            return responseData;
        } catch (error) {
            // Remove from active requests on error
            this.activeRequests.delete(abortController);
            
            // AbortError is expected when cancelAllRequests() runs (logout / navigation).
            // Rethrow as-is so callers can detect err.name === 'AbortError' and skip logging.
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('Request aborted:', fullUrl);
                throw error;
            }
            
            throw error;
        }
    }

    async get<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('GET', path, { params, headers });
    }

    async post<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('POST', path, { body, params, headers });
    }

    async put<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PUT', path, { body, params, headers });
    }

    async patch<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PATCH', path, { body, params, headers });
    }

    async delete<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('DELETE', path, { params, headers });
    }

    get url(): string {
        return this.baseUrl;
    }
}
